import { Inbox, SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'

/* ─────────────────────────────────────────────────────────────────────────────
 *  NOTHING YET IS NOT NOTHING MATCHES (UX-DR-17)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two states look identical on screen — a table with no rows — and mean
 * opposite things:
 *
 *   * NOTHING YET — the dataset is genuinely empty. Nothing is wrong and there
 *     is nothing to undo. State it plainly, as good news where it is good news
 *     ("no losing shipments") and as a neutral fact otherwise.
 *   * NOTHING MATCHES — the dataset has rows and a filter narrowed it to zero.
 *     The user's own narrowing is hiding their data, so the message says so
 *     AND offers the way back.
 *
 * Conflating them is the specific failure the criterion exists to prevent: a
 * founder who filters receivables to last month, reads "No receivables", and
 * concludes the company is owed nothing. This component is the ONE place the
 * distinction is decided, so the five finance views cannot each answer it
 * differently — and DataTable renders it by default, so a view has to go out of
 * its way to get it wrong.
 */

export interface TableEmptyStateProps {
  /** True when a filter — a date range, a facet, a search — is what emptied
   *  the table. The caller knows this and the table cannot: an empty response
   *  looks the same either way from here. */
  filtered: boolean
  /** Clears every filter on the surface. Absent when the surface has no way
   *  to clear (a range control with no "all" option, say), and then the
   *  message still names the filter as the cause rather than offering a button
   *  that does nothing. */
  onClearFilters?: () => void
  /** The *nothing yet* wording. Say what fills this surface, or say plainly
   *  that its emptiness is good news. Defaults to the generic list copy. */
  title?: string
  description?: string
}

export function TableEmptyState({
  filtered,
  onClearFilters,
  title,
  description,
}: TableEmptyStateProps) {
  const { t } = useTranslation()

  if (filtered) {
    return (
      <EmptyState
        icon={SearchX}
        title={t('common.filters.emptyTitle')}
        description={t('common.filters.emptyDescription')}
        action={
          onClearFilters ? (
            <Button variant="secondary" size="sm" onClick={onClearFilters}>
              {t('common.filters.clearAll')}
            </Button>
          ) : null
        }
      />
    )
  }

  return (
    <EmptyState
      icon={Inbox}
      title={title ?? t('common.table.emptyTitle')}
      description={description ?? t('common.table.emptyDescription')}
    />
  )
}
